import { useMemo } from 'react'

import { describeSummaryValue } from '@fit-editor/fit-core'
import type { Activity, Provenance } from '@fit-editor/fit-core'

import { Badge } from '@/components/ui/badge'
import { Tooltip } from '@/components/ui/tooltip'
import { useUnits } from '@/hooks/use-units'
import { computeSelectionStats } from '@/lib/selection-stats'
import { cn } from '@/lib/utils'
import type { TimeRangeMs } from '@/store/activity-store'

/**
 * Compact provenance markers. Same theme-token treatment as the
 * dashboard's `ProvenanceStat` (dashed border = estimated, so the
 * distinction never relies on color alone), shortened for a one-line
 * strip; the full word is available to screen readers and the full
 * explanation in the tooltip.
 */
const PROVENANCE_SHORT: Record<Exclude<Provenance, 'unavailable'>, string> = {
  original: 'orig',
  recalculated: 'calc',
  estimated: 'est',
  edited: 'edit',
}
const PROVENANCE_LABEL: Record<Provenance, string> = {
  original: 'Original',
  recalculated: 'Recalculated',
  estimated: 'Estimated',
  edited: 'Edited',
  unavailable: 'Unavailable',
}
const PROVENANCE_CLASSNAME: Record<Exclude<Provenance, 'unavailable'>, string> = {
  original: 'border-border bg-background text-foreground',
  recalculated: 'border-primary/40 bg-primary/10 text-primary',
  estimated: 'border-dashed border-border bg-accent text-accent-foreground',
  edited: 'border-warning/60 bg-warning/10 text-warning-text underline decoration-dotted',
}

export interface SelectionStatsStripProps {
  activity: Activity
  range: TimeRangeMs
  /** Terrain overlay values index-aligned to `activity.records`, when loaded. */
  terrainValues?: (number | null)[] | undefined
}

/**
 * Stats for the timeline's current range selection, recomputed (on the
 * main thread -- these are linear passes over the selected records only)
 * whenever the activity or the range changes.
 */
export function SelectionStatsStrip({ activity, range, terrainValues }: SelectionStatsStripProps) {
  const units = useUnits()
  const stats = useMemo(
    () => computeSelectionStats(activity, range, terrainValues, units),
    [activity, range, terrainValues, units],
  )

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Selection statistics"
      className="border-border bg-muted/40 rounded-md border px-2 py-1"
    >
      <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {stats.map((s) => {
          const description = describeSummaryValue(s.value)
          const provenance = s.value.provenance
          return (
            <div key={s.key} className="flex items-center gap-1">
              <dt className="text-muted-foreground">{s.label}</dt>
              <dd className="flex items-center gap-1">
                {provenance === 'unavailable' ? (
                  <Tooltip content={description.explanation}>
                    <span className="text-muted-foreground italic">{s.display}</span>
                  </Tooltip>
                ) : (
                  <>
                    <span className="text-foreground font-semibold">{s.display}</span>
                    <Tooltip content={description.explanation}>
                      <Badge
                        className={cn(
                          'px-1 py-0 text-[10px] leading-4',
                          PROVENANCE_CLASSNAME[provenance],
                        )}
                      >
                        <span aria-hidden="true">{PROVENANCE_SHORT[provenance]}</span>
                        <span className="sr-only">{PROVENANCE_LABEL[provenance]}</span>
                      </Badge>
                    </Tooltip>
                  </>
                )}
              </dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}
